import React, { useState, useEffect } from 'react';
import { supabase } from '../lib/supabase';
import { useAuth } from '../context/AuthContext';
import {
  Users,
  Gift,
  Plus,
  Edit,
  Trash2,
  X,
  Check,
  RefreshCw,
  PackageCheck,
  Clock,
  Ban,
  Settings,
  Award,
  Upload,
  Image
} from 'lucide-react';
import { format } from 'date-fns';
import { es } from 'date-fns/locale';
import { useToast } from '../hooks/useToast';

const isAdmin = (role: string | null) => role === 'Administrador' || role === 'Super Admin';

interface EmployeeRow {
  user_id: string;
  email: string;
  role: string;
  points_balance: number;
  points_6m: number;
  current_level: string;
}

interface Level {
  id: string;
  level_name: string;
  min_points: number;
  max_points: number | null;
  color_primary: string;
  color_secondary: string;
  benefits: string[];
  is_active: boolean;
  sort_order: number;
}

interface Reward {
  id: string;
  name: string;
  description: string;
  points_cost: number;
  category: string;
  is_active: boolean;
  image_url?: string;
}

interface RedemptionRequest {
  id: string;
  user_id: string;
  user_email: string;
  reward_name: string;
  points_spent: number;
  status: string;
  created_at: string;
}

interface PointsConfig {
  id?: string;
  points_per_1000_invoice: number;
  points_per_1000_credit_note: number;
  points_per_1000_on_time: number;
  points_per_1000_late_1_3_days: number;
}

interface EmployeeHistoryItem {
  user_id: string;
  user_email: string;
  fecha: string;
  tipo: string;
  motivo: string;
  puntos: number;
  conversion: string;
  canje: string | null;
  estado_canje: string | null;
  source_type: string;
  source_id: string | null;
}

interface Category {
  id: string;
  name: string;
  sort_order: number;
}

const EmployeePointsConfig: React.FC = () => {
  const { userRole } = useAuth();
  const { toast, ToastComponent } = useToast();
  const [employees, setEmployees] = useState<EmployeeRow[]>([]);
  const [rewards, setRewards] = useState<Reward[]>([]);
  const [levels, setLevels] = useState<Level[]>([]);
  const [categories, setCategories] = useState<Category[]>([]);
  const [pointsConfig, setPointsConfig] = useState<PointsConfig | null>(null);
  const [redemptionRequests, setRedemptionRequests] = useState<RedemptionRequest[]>([]);
  const [historyRows, setHistoryRows] = useState<EmployeeHistoryItem[]>([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState<'empleados' | 'niveles' | 'config' | 'premios' | 'canjes' | 'historial'>('empleados');
  const [historyUserFilter, setHistoryUserFilter] = useState<string>('all');

  const [adjustModal, setAdjustModal] = useState<{ show: boolean; user: EmployeeRow | null }>({ show: false, user: null });
  const [adjustAmount, setAdjustAmount] = useState('');
  const [adjustReason, setAdjustReason] = useState('');
  const [saving, setSaving] = useState(false);

  const [rewardModal, setRewardModal] = useState<{ show: boolean; editing: Reward | null }>({ show: false, editing: null });
  const [rewardForm, setRewardForm] = useState({ name: '', description: '', points_cost: '', category: 'general', image_url: '' });
  const [rewardImageFile, setRewardImageFile] = useState<File | null>(null);

  const [levelModal, setLevelModal] = useState<{ show: boolean; editing: Level | null }>({ show: false, editing: null });
  const [levelForm, setLevelForm] = useState<Partial<Level>>({});

  const [categoryModal, setCategoryModal] = useState(false);
  const [newCategoryName, setNewCategoryName] = useState('');

  useEffect(() => {
    if (isAdmin(userRole)) loadAll();
  }, [userRole]);

  useEffect(() => {
    if (!isAdmin(userRole)) return;
    void loadHistory(historyUserFilter);
  }, [historyUserFilter, userRole]);

  const loadHistory = async (userFilter: string) => {
    try {
      setHistoryLoading(true);
      const p_user_id = userFilter === 'all' ? null : userFilter;
      const { data, error } = await supabase.rpc('admin_get_employee_points_history', {
        p_user_id,
        p_limit: 2000
      });
      if (error) throw error;
      setHistoryRows(Array.isArray(data) ? (data as EmployeeHistoryItem[]) : []);
    } catch (e: any) {
      console.error('Error loading employee history:', e);
      toast.error(
        'No se pudo cargar el historial',
        e?.message || e?.details || 'Verificá que la migración del historial esté aplicada (supabase db push).'
      );
      setHistoryRows([]);
    } finally {
      setHistoryLoading(false);
    }
  };

  const loadAll = async () => {
    setLoading(true);
    try {
      const [empRes, rewRes, redRes, levRes, catRes, cfgRes] = await Promise.all([
        supabase.rpc('admin_list_employee_points'),
        supabase.from('employee_rewards').select('*').order('points_cost'),
        supabase.rpc('admin_list_employee_redemption_requests'),
        supabase.from('employee_points_levels').select('*').order('sort_order'),
        supabase.from('employee_reward_categories').select('*').order('sort_order'),
        supabase.from('employee_points_config').select('*').order('updated_at', { ascending: false }).limit(1).maybeSingle()
      ]);
      if (empRes.data) setEmployees(empRes.data as EmployeeRow[]);
      if (rewRes.data) setRewards(rewRes.data);
      if (redRes.data) setRedemptionRequests(redRes.data as RedemptionRequest[]);
      if (levRes.data) setLevels(levRes.data);
      if (catRes.data) setCategories(catRes.data);
      if (cfgRes.data) {
        setPointsConfig({
          points_per_1000_invoice: cfgRes.data.points_per_1000_invoice ?? 1,
          points_per_1000_credit_note: cfgRes.data.points_per_1000_credit_note ?? -1,
          points_per_1000_on_time: cfgRes.data.points_per_1000_on_time ?? 1,
          points_per_1000_late_1_3_days: parseFloat(cfgRes.data.points_per_1000_late_1_3_days) ?? 0.5
        });
      } else {
        setPointsConfig({ points_per_1000_invoice: 1, points_per_1000_credit_note: -1, points_per_1000_on_time: 1, points_per_1000_late_1_3_days: 0.5 });
      }
      await loadHistory(historyUserFilter);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  const handleAdjust = async () => {
    if (!adjustModal.user || !adjustAmount) return;
    const amt = parseInt(adjustAmount);
    if (isNaN(amt) || amt === 0) {
      alert('Ingresá un monto válido (positivo para sumar, negativo para restar)');
      return;
    }
    setSaving(true);
    try {
      const { data, error } = await supabase.rpc('admin_adjust_employee_points', {
        p_user_id: adjustModal.user.user_id,
        p_amount: amt,
        p_reason: adjustReason || null
      });
      if (error) throw error;
      const res = data as { success: boolean; error?: string };
      if (res?.success) {
        setAdjustModal({ show: false, user: null });
        setAdjustAmount('');
        setAdjustReason('');
        loadAll();
      } else throw new Error(res?.error);
    } catch (e: any) {
      alert(e.message || 'Error al ajustar');
    } finally {
      setSaving(false);
    }
  };

  const handleUpdateRedemptionStatus = async (id: string, status: 'delivered' | 'rejected') => {
    setSaving(true);
    try {
      const { data, error } = await supabase.rpc('admin_update_employee_redemption_status', {
        p_redemption_id: id,
        p_new_status: status
      });
      if (error) throw error;
      const res = data as { success: boolean; error?: string };
      if (res?.success) loadAll();
      else throw new Error(res?.error);
    } catch (e: any) {
      alert(e.message || 'Error');
    } finally {
      setSaving(false);
    }
  };

  const uploadRewardImage = async (): Promise<string | null> => {
    if (!rewardImageFile) return rewardForm.image_url || null;
    try {
      const ext = rewardImageFile.name.split('.').pop();
      const path = `images/${Date.now()}-${Math.random().toString(36).slice(2)}.${ext}`;
      const { error } = await supabase.storage.from('employee-rewards').upload(path, rewardImageFile, { upsert: true });
      if (error) throw error;
      const { data } = supabase.storage.from('employee-rewards').getPublicUrl(path);
      return data.publicUrl;
    } catch (e: any) {
      alert(e.message || 'Error al subir imagen');
      return null;
    }
  };

  const saveReward = async () => {
    if (!rewardForm.name || !rewardForm.points_cost) {
      alert('Nombre y costo en puntos son obligatorios');
      return;
    }
    const pts = parseInt(rewardForm.points_cost);
    if (isNaN(pts) || pts < 1) {
      alert('Costo debe ser un número mayor a 0');
      return;
    }
    setSaving(true);
    try {
      const imageUrl = await uploadRewardImage();
      const payload = {
        name: rewardForm.name,
        description: rewardForm.description || '',
        points_cost: pts,
        category: rewardForm.category || 'general',
        is_active: true,
        image_url: imageUrl || rewardForm.image_url || null,
        updated_at: new Date().toISOString()
      };
      if (rewardModal.editing) {
        await supabase.from('employee_rewards').update(payload).eq('id', rewardModal.editing.id);
      } else {
        await supabase.from('employee_rewards').insert({ ...payload, updated_at: undefined });
      }
      setRewardModal({ show: false, editing: null });
      setRewardForm({ name: '', description: '', points_cost: '', category: 'general', image_url: '' });
      setRewardImageFile(null);
      loadAll();
    } catch (e: any) {
      alert(e.message || 'Error');
    } finally {
      setSaving(false);
    }
  };

  const deleteReward = async (id: string) => {
    if (!confirm('¿Eliminar este premio?')) return;
    try {
      await supabase.from('employee_rewards').delete().eq('id', id);
      loadAll();
    } catch (e: any) {
      alert(e.message);
    }
  };

  const openEditReward = (r: Reward) => {
    setRewardForm({
      name: r.name,
      description: r.description || '',
      points_cost: r.points_cost.toString(),
      category: r.category || 'general',
      image_url: r.image_url || ''
    });
    setRewardImageFile(null);
    setRewardModal({ show: true, editing: r });
  };

  const saveLevel = async () => {
    if (!levelForm.level_name || levelForm.min_points == null) {
      alert('Nombre y puntos mínimos son obligatorios');
      return;
    }
    setSaving(true);
    try {
      const payload = {
        level_name: levelForm.level_name,
        min_points: levelForm.min_points,
        max_points: levelForm.max_points ?? null,
        color_primary: levelForm.color_primary || '#6366f1',
        color_secondary: levelForm.color_secondary || '#ffffff',
        benefits: levelForm.benefits || [],
        is_active: levelForm.is_active ?? true,
        sort_order: levelForm.sort_order ?? 0,
        updated_at: new Date().toISOString()
      };
      if (levelModal.editing) {
        await supabase.from('employee_points_levels').update(payload).eq('id', levelModal.editing.id);
      } else {
        await supabase.from('employee_points_levels').insert({ ...payload, updated_at: undefined });
      }
      setLevelModal({ show: false, editing: null });
      setLevelForm({});
      loadAll();
    } catch (e: any) {
      alert(e.message || 'Error');
    } finally {
      setSaving(false);
    }
  };

  const deleteLevel = async (id: string) => {
    if (!confirm('¿Eliminar este nivel?')) return;
    try {
      await supabase.from('employee_points_levels').delete().eq('id', id);
      loadAll();
    } catch (e: any) {
      alert(e.message);
    }
  };

  const openEditLevel = (l?: Level) => {
    if (l) {
      setLevelForm({ ...l });
      setLevelModal({ show: true, editing: l });
    } else {
      setLevelForm({ level_name: '', min_points: 0, max_points: null, color_primary: '#6366f1', color_secondary: '#ffffff', benefits: [], is_active: true, sort_order: levels.length });
      setLevelModal({ show: true, editing: null });
    }
  };

  const savePointsConfig = async () => {
    if (!pointsConfig) return;
    setSaving(true);
    try {
      const { data: existing } = await supabase.from('employee_points_config').select('id').limit(1).maybeSingle();
      const payload = {
        points_per_1000_invoice: pointsConfig.points_per_1000_invoice,
        points_per_1000_credit_note: pointsConfig.points_per_1000_credit_note,
        points_per_1000_on_time: pointsConfig.points_per_1000_on_time,
        points_per_1000_late_1_3_days: pointsConfig.points_per_1000_late_1_3_days,
        updated_at: new Date().toISOString()
      };
      if (existing?.id) {
        await supabase.from('employee_points_config').update(payload).eq('id', existing.id);
      } else {
        await supabase.from('employee_points_config').insert(payload);
      }
      // Tras guardar reglas, recalcular todas las transacciones automáticas para que
      // los puntos reflejen la nueva configuración inmediatamente.
      const { data: rebuild, error: rebuildError } = await supabase.rpc('rebuild_employee_points_transactions');
      if (rebuildError) throw rebuildError;
      const res = rebuild as { success: boolean; error?: string } | null;
      if (res && res.success === false) throw new Error(res.error || 'No se pudo recalcular');
      toast.success('Reglas guardadas y puntos recalculados');
      loadAll();
    } catch (e: any) {
      toast.error('No se pudo guardar', e?.message || 'Error desconocido');
    } finally {
      setSaving(false);
    }
  };

  const handleRebuildPoints = async () => {
    if (!confirm('Esto recalcula TODAS las transacciones automáticas de puntos (facturas, NC, producciones, muestras) desde cero usando la configuración actual. Los ajustes manuales y canjes se preservan. ¿Continuar?')) return;
    setSaving(true);
    try {
      const { data, error } = await supabase.rpc('rebuild_employee_points_transactions');
      if (error) throw error;
      const res = data as { success: boolean; error?: string; invoices_processed?: number; credit_notes_processed?: number; productions_processed?: number; samples_processed?: number };
      if (!res?.success) throw new Error(res?.error || 'No se pudo recalcular');
      toast.success(
        'Puntos recalculados',
        `Facturas: ${res.invoices_processed ?? 0} · NC: ${res.credit_notes_processed ?? 0} · Producciones: ${res.productions_processed ?? 0} · Muestras: ${res.samples_processed ?? 0}`
      );
      loadAll();
    } catch (e: any) {
      toast.error('Error al recalcular puntos', e?.message || 'Error desconocido');
    } finally {
      setSaving(false);
    }
  };

  const addCategory = async () => {
    if (!newCategoryName.trim()) return;
    setSaving(true);
    try {
      await supabase.from('employee_reward_categories').insert({
        name: newCategoryName.trim().toLowerCase().replace(/\s+/g, '-'),
        sort_order: categories.length
      });
      setCategoryModal(false);
      setNewCategoryName('');
      loadAll();
    } catch (e: any) {
      alert(e.message || 'Error');
    } finally {
      setSaving(false);
    }
  };

  if (!isAdmin(userRole)) {
    return (
      <div className="p-6 text-center text-gray-500">
        No tenés permisos para acceder a esta sección.
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <RefreshCw className="h-8 w-8 animate-spin text-indigo-500" />
      </div>
    );
  }

  const pending = redemptionRequests.filter(r => r.status === 'pending');
  const rejected = redemptionRequests.filter(r => r.status === 'rejected');
  const delivered = redemptionRequests.filter(r => r.status === 'delivered');

  const tabs = [
    { id: 'empleados' as const, label: 'Empleados', icon: Users },
    { id: 'niveles' as const, label: 'Niveles', icon: Award },
    { id: 'config' as const, label: 'Reglas puntos', icon: Settings },
    { id: 'premios' as const, label: 'Premios', icon: Gift },
    { id: 'canjes' as const, label: 'Canjes', icon: PackageCheck },
    { id: 'historial' as const, label: 'Historial', icon: Clock }
  ];

  const filteredHistory = historyRows;

  return (
    <div className="max-w-6xl mx-auto p-6 space-y-6">
      {ToastComponent}
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-semibold text-gray-900 dark:text-white">Puntos de Colaboradores</h1>
        <button onClick={loadAll} className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800">
          <RefreshCw className="h-5 w-5" />
        </button>
      </div>

      <div className="flex gap-2 border-b border-gray-200 dark:border-gray-700 overflow-x-auto">
        {tabs.map(tab => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`px-4 py-2 text-sm font-medium rounded-t-lg transition-colors shrink-0 ${
              activeTab === tab.id
                ? 'bg-indigo-100 text-indigo-700 dark:bg-indigo-900/30 dark:text-indigo-300 border-b-2 border-indigo-500'
                : 'text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-800'
            }`}
          >
            <tab.icon className="inline h-4 w-4 mr-1.5" />{tab.label}
          </button>
        ))}
      </div>

      {activeTab === 'empleados' && (
        <div className="rounded-xl border border-gray-200 dark:border-gray-700 overflow-hidden bg-white dark:bg-gray-900">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 dark:bg-gray-800/50">
              <tr>
                <th className="px-4 py-3 text-left text-gray-500 dark:text-gray-400">Email</th>
                <th className="px-4 py-3 text-left text-gray-500 dark:text-gray-400">Rol</th>
                <th className="px-4 py-3 text-right text-gray-500 dark:text-gray-400">Puntos</th>
                <th className="px-4 py-3 text-left text-gray-500 dark:text-gray-400">Nivel</th>
                <th className="px-4 py-3 w-24" />
              </tr>
            </thead>
            <tbody>
              {employees.map(emp => (
                <tr key={emp.user_id} className="border-t border-gray-100 dark:border-gray-800">
                  <td className="px-4 py-3 text-gray-900 dark:text-white">{emp.email}</td>
                  <td className="px-4 py-3 text-gray-600 dark:text-gray-300">{emp.role}</td>
                  <td className="px-4 py-3 text-right font-medium">{emp.points_balance}</td>
                  <td className="px-4 py-3 text-gray-600 dark:text-gray-300">{emp.current_level || '-'}</td>
                  <td className="px-4 py-3">
                    <button onClick={() => setAdjustModal({ show: true, user: emp })} className="text-indigo-600 hover:text-indigo-500 text-xs font-medium">Ajustar</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {activeTab === 'niveles' && (
        <div>
          <div className="flex justify-end mb-4">
            <button onClick={() => openEditLevel()} className="flex items-center gap-2 px-4 py-2 rounded-lg bg-indigo-600 text-white text-sm font-medium hover:bg-indigo-700">
              <Plus className="h-4 w-4" /> Nuevo nivel
            </button>
          </div>
          <div className="space-y-3">
            {levels.map(l => (
              <div key={l.id} className="rounded-xl border border-gray-200 dark:border-gray-700 p-4 flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg" style={{ backgroundColor: l.color_primary }} />
                  <div>
                    <p className="font-medium text-gray-900 dark:text-white">{l.level_name}</p>
                    <p className="text-sm text-gray-500">{l.min_points} - {l.max_points ?? '∞'} pts</p>
                  </div>
                </div>
                <div className="flex gap-2">
                  <button onClick={() => openEditLevel(l)} className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800"><Edit className="h-4 w-4" /></button>
                  <button onClick={() => deleteLevel(l.id)} className="p-2 rounded-lg hover:bg-red-50 dark:hover:bg-red-900/20 text-red-600"><Trash2 className="h-4 w-4" /></button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {activeTab === 'config' && pointsConfig && (
        <div className="rounded-xl border border-gray-200 dark:border-gray-700 p-6 space-y-6 bg-white dark:bg-gray-900">
          <div>
            <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">Vendedor</h3>
            <div className="grid gap-4 sm:grid-cols-2">
              <div>
                <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1.5">Puntos por cada $1000 de factura</label>
                <input type="number" value={pointsConfig.points_per_1000_invoice} onChange={e => setPointsConfig(c => c ? { ...c, points_per_1000_invoice: parseInt(e.target.value) || 0 } : c)} className="w-full px-3 py-2 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800" />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1.5">Puntos por cada $1000 de nota de crédito</label>
                <input type="number" value={pointsConfig.points_per_1000_credit_note} onChange={e => setPointsConfig(c => c ? { ...c, points_per_1000_credit_note: parseInt(e.target.value) || 0 } : c)} className="w-full px-3 py-2 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800" />
                <p className="text-xs text-gray-400 mt-1">Usar valor negativo (ej: -1) para restar</p>
              </div>
            </div>
          </div>
          <div>
            <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">Producción</h3>
            <div className="grid gap-4 sm:grid-cols-2">
              <div>
                <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1.5">Puntos por cada $1000 entregado a tiempo</label>
                <input type="number" step="0.5" value={pointsConfig.points_per_1000_on_time} onChange={e => setPointsConfig(c => c ? { ...c, points_per_1000_on_time: parseFloat(e.target.value) || 0 } : c)} className="w-full px-3 py-2 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800" />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1.5">Puntos por cada $1000 entregado con 1-3 días de demora</label>
                <input type="number" step="0.5" value={pointsConfig.points_per_1000_late_1_3_days} onChange={e => setPointsConfig(c => c ? { ...c, points_per_1000_late_1_3_days: parseFloat(e.target.value) || 0 } : c)} className="w-full px-3 py-2 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800" />
              </div>
            </div>
          </div>
          <div className="flex flex-wrap gap-2">
            <button onClick={savePointsConfig} disabled={saving} className="px-4 py-2 rounded-lg bg-indigo-600 text-white font-medium hover:bg-indigo-700 disabled:opacity-50">
              Guardar reglas
            </button>
            <button
              onClick={handleRebuildPoints}
              disabled={saving}
              className="px-4 py-2 rounded-lg border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-200 font-medium hover:bg-gray-100 dark:hover:bg-gray-800 disabled:opacity-50"
              title="Recalcular todas las transacciones automáticas usando la configuración actual"
            >
              Recalcular puntos
            </button>
          </div>
          <p className="text-xs text-gray-500 dark:text-gray-400">
            Al guardar las reglas, los puntos de todos los colaboradores se recalculan automáticamente usando los nuevos valores. Los ajustes manuales y canjes se preservan.
          </p>
        </div>
      )}

      {activeTab === 'premios' && (
        <div>
          <div className="flex justify-end gap-2 mb-4">
            <button onClick={() => { setRewardForm({ name: '', description: '', points_cost: '', category: 'general', image_url: '' }); setRewardImageFile(null); setRewardModal({ show: true, editing: null }); }} className="flex items-center gap-2 px-4 py-2 rounded-lg bg-indigo-600 text-white text-sm font-medium hover:bg-indigo-700">
              <Plus className="h-4 w-4" /> Nuevo premio
            </button>
            <button onClick={() => setCategoryModal(true)} className="flex items-center gap-2 px-4 py-2 rounded-lg border border-gray-200 dark:border-gray-700 text-sm font-medium hover:bg-gray-100 dark:hover:bg-gray-800">
              Nueva categoría
            </button>
          </div>
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
            {rewards.map(r => (
              <div key={r.id} className="rounded-xl border border-gray-200 dark:border-gray-700 overflow-hidden flex flex-col bg-white dark:bg-gray-900">
                <div className="aspect-video bg-gray-100 dark:bg-gray-800 flex items-center justify-center">
                  {r.image_url ? (
                    <img src={r.image_url} alt={r.name} className="w-full h-full object-cover" />
                  ) : (
                    <Image className="h-12 w-12 text-gray-400" />
                  )}
                </div>
                <div className="p-4 flex justify-between items-start">
                  <div>
                    <p className="font-medium text-gray-900 dark:text-white">{r.name}</p>
                    <p className="text-sm text-gray-500 dark:text-gray-400">{r.points_cost} pts · {r.category}</p>
                  </div>
                  <div className="flex gap-1">
                    <button onClick={() => openEditReward(r)} className="p-1.5 rounded hover:bg-gray-100 dark:hover:bg-gray-800"><Edit className="h-4 w-4" /></button>
                    <button onClick={() => deleteReward(r.id)} className="p-1.5 rounded hover:bg-red-50 dark:hover:bg-red-900/20 text-red-600"><Trash2 className="h-4 w-4" /></button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {activeTab === 'canjes' && (
        <div className="grid gap-4 md:grid-cols-3">
          <div className="rounded-xl border border-amber-200 dark:border-amber-800 bg-amber-50/50 dark:bg-amber-900/10 p-4">
            <h3 className="flex items-center gap-2 font-medium text-amber-800 dark:text-amber-300 mb-3"><Clock className="h-4 w-4" /> Pendientes ({pending.length})</h3>
            <div className="space-y-2">
              {pending.map(r => (
                <div key={r.id} className="rounded-lg border border-amber-200 dark:border-amber-800 p-3 bg-white dark:bg-gray-900">
                  <p className="text-sm font-medium">{r.user_email}</p>
                  <p className="text-xs text-gray-500">{r.reward_name} · {r.points_spent} pts</p>
                  <p className="text-xs text-gray-400 mt-1">{format(new Date(r.created_at), 'dd/MM HH:mm', { locale: es })}</p>
                  <div className="mt-2 flex gap-2">
                    <button onClick={() => handleUpdateRedemptionStatus(r.id, 'delivered')} disabled={saving} className="flex-1 py-1 text-xs rounded bg-emerald-600 text-white hover:bg-emerald-700 disabled:opacity-50">Entregar</button>
                    <button onClick={() => handleUpdateRedemptionStatus(r.id, 'rejected')} disabled={saving} className="flex-1 py-1 text-xs rounded bg-red-600 text-white hover:bg-red-700 disabled:opacity-50">Rechazar</button>
                  </div>
                </div>
              ))}
            </div>
          </div>
          <div className="rounded-xl border border-emerald-200 dark:border-emerald-800 bg-emerald-50/50 dark:bg-emerald-900/10 p-4">
            <h3 className="flex items-center gap-2 font-medium text-emerald-800 dark:text-emerald-300 mb-3"><Check className="h-4 w-4" /> Entregados ({delivered.length})</h3>
            <div className="space-y-2">
              {delivered.map(r => (
                <div key={r.id} className="rounded-lg border border-emerald-200 dark:border-emerald-800 p-3 bg-white dark:bg-gray-900 text-sm">
                  <p className="font-medium">{r.user_email}</p>
                  <p className="text-xs text-gray-500">{r.reward_name} · {r.points_spent} pts</p>
                </div>
              ))}
            </div>
          </div>
          <div className="rounded-xl border border-gray-200 dark:border-gray-700 p-4">
            <h3 className="flex items-center gap-2 font-medium text-gray-600 dark:text-gray-400 mb-3"><Ban className="h-4 w-4" /> Rechazados ({rejected.length})</h3>
            <div className="space-y-2">
              {rejected.map(r => (
                <div key={r.id} className="rounded-lg border border-gray-200 dark:border-gray-700 p-3 bg-white dark:bg-gray-900 text-sm">
                  <p className="font-medium">{r.user_email}</p>
                  <p className="text-xs text-gray-500">{r.reward_name} · {r.points_spent} pts</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {activeTab === 'historial' && (
        <div className="rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 overflow-hidden">
          <div className="p-4 border-b border-gray-200 dark:border-gray-700 flex flex-col sm:flex-row sm:items-center gap-3 sm:justify-between">
            <div>
              <h3 className="text-sm font-semibold text-gray-900 dark:text-white">Historial de puntos</h3>
              <p className="text-xs text-gray-500 dark:text-gray-400">
                Muestra fecha de asignación, motivo, conversión aplicada y canjes.
              </p>
            </div>
            <div className="flex items-center gap-2">
              <label className="text-xs text-gray-500 dark:text-gray-400">Colaborador</label>
              <select
                value={historyUserFilter}
                onChange={e => setHistoryUserFilter(e.target.value)}
                className="px-3 py-2 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-sm"
              >
                <option value="all">Todos</option>
                {employees.map(emp => (
                  <option key={emp.user_id} value={emp.user_id}>{emp.email}</option>
                ))}
              </select>
            </div>
          </div>

          {historyLoading ? (
            <div className="p-6 text-sm text-gray-500 dark:text-gray-400">Cargando movimientos...</div>
          ) : filteredHistory.length === 0 ? (
            <div className="p-6 text-sm text-gray-500 dark:text-gray-400">Sin movimientos en el historial.</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm min-w-[980px]">
                <thead className="bg-gray-50 dark:bg-gray-800/50">
                  <tr>
                    <th className="px-4 py-3 text-left text-gray-500 dark:text-gray-400">Fecha</th>
                    <th className="px-4 py-3 text-left text-gray-500 dark:text-gray-400">Colaborador</th>
                    <th className="px-4 py-3 text-left text-gray-500 dark:text-gray-400">Tipo</th>
                    <th className="px-4 py-3 text-left text-gray-500 dark:text-gray-400">Motivo</th>
                    <th className="px-4 py-3 text-left text-gray-500 dark:text-gray-400">Conversión</th>
                    <th className="px-4 py-3 text-left text-gray-500 dark:text-gray-400">Canje</th>
                    <th className="px-4 py-3 text-left text-gray-500 dark:text-gray-400">Estado</th>
                    <th className="px-4 py-3 text-right text-gray-500 dark:text-gray-400">Puntos</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredHistory.map((h, idx) => (
                    <tr key={`${h.source_type}-${h.source_id ?? idx}-${h.fecha}`} className="border-t border-gray-100 dark:border-gray-800">
                      <td className="px-4 py-3 text-gray-600 dark:text-gray-300 whitespace-nowrap">
                        {h.fecha ? format(new Date(h.fecha), 'dd/MM/yyyy HH:mm', { locale: es }) : '-'}
                      </td>
                      <td className="px-4 py-3 text-gray-700 dark:text-gray-300">{h.user_email}</td>
                      <td className="px-4 py-3 text-gray-700 dark:text-gray-300 whitespace-nowrap">{h.tipo}</td>
                      <td className="px-4 py-3 text-gray-700 dark:text-gray-300">{h.motivo || '-'}</td>
                      <td className="px-4 py-3 text-gray-600 dark:text-gray-400">{h.conversion || '-'}</td>
                      <td className="px-4 py-3 text-gray-700 dark:text-gray-300">{h.canje || '-'}</td>
                      <td className="px-4 py-3">
                        {h.estado_canje ? (
                          <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                            h.estado_canje === 'pending'
                              ? 'bg-amber-100 text-amber-700 dark:bg-amber-900/20 dark:text-amber-300'
                              : h.estado_canje === 'delivered'
                                ? 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/20 dark:text-emerald-300'
                                : 'bg-red-100 text-red-700 dark:bg-red-900/20 dark:text-red-300'
                          }`}>
                            {h.estado_canje}
                          </span>
                        ) : (
                          <span className="text-gray-400">-</span>
                        )}
                      </td>
                      <td className={`px-4 py-3 text-right font-medium whitespace-nowrap ${h.puntos >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>
                        {h.puntos >= 0 ? '+' : ''}{h.puntos}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* Modal ajuste */}
      {adjustModal.show && adjustModal.user && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50" onClick={() => !saving && setAdjustModal({ show: false, user: null })}>
          <div className="bg-white dark:bg-gray-900 rounded-2xl p-6 max-w-sm w-full" onClick={e => e.stopPropagation()}>
            <h3 className="text-lg font-semibold mb-4">Ajustar puntos · {adjustModal.user.email}</h3>
            <input type="number" placeholder="Cantidad (+ o -)" value={adjustAmount} onChange={e => setAdjustAmount(e.target.value)} className="w-full px-3 py-2 rounded-lg border border-gray-200 dark:border-gray-700 mb-3" />
            <input type="text" placeholder="Motivo (opcional)" value={adjustReason} onChange={e => setAdjustReason(e.target.value)} className="w-full px-3 py-2 rounded-lg border border-gray-200 dark:border-gray-700 mb-4" />
            <div className="flex gap-2">
              <button onClick={() => !saving && setAdjustModal({ show: false, user: null })} className="flex-1 py-2 rounded-lg border">Cancelar</button>
              <button onClick={handleAdjust} disabled={saving} className="flex-1 py-2 rounded-lg bg-indigo-600 text-white disabled:opacity-50">Aplicar</button>
            </div>
          </div>
        </div>
      )}

      {/* Modal premio */}
      {rewardModal.show && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 overflow-y-auto" onClick={() => !saving && setRewardModal({ show: false, editing: null })}>
          <div className="bg-white dark:bg-gray-900 rounded-2xl p-6 max-w-md w-full my-8" onClick={e => e.stopPropagation()}>
            <h3 className="text-lg font-semibold mb-4">{rewardModal.editing ? 'Editar premio' : 'Nuevo premio'}</h3>

            <div className="mb-4">
              <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1.5">Imagen</label>
              <div className="flex gap-3 items-center">
                {(rewardForm.image_url || rewardImageFile) && (
                  <div className="w-20 h-20 rounded-lg border overflow-hidden bg-gray-100 dark:bg-gray-800">
                    {rewardImageFile ? (
                      <img src={URL.createObjectURL(rewardImageFile)} alt="" className="w-full h-full object-cover" />
                    ) : rewardForm.image_url ? (
                      <img src={rewardForm.image_url} alt="" className="w-full h-full object-cover" />
                    ) : null}
                  </div>
                )}
                <label className="flex items-center gap-2 px-3 py-2 rounded-lg border border-gray-200 dark:border-gray-700 cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-800">
                  <Upload className="h-4 w-4" />
                  <span className="text-sm">Subir imagen</span>
                  <input type="file" accept="image/*" className="hidden" onChange={e => e.target.files?.[0] && setRewardImageFile(e.target.files[0])} />
                </label>
              </div>
            </div>

            <input type="text" placeholder="Nombre" value={rewardForm.name} onChange={e => setRewardForm(f => ({ ...f, name: e.target.value }))} className="w-full px-3 py-2 rounded-lg border border-gray-200 dark:border-gray-700 mb-3" />
            <input type="text" placeholder="Descripción" value={rewardForm.description} onChange={e => setRewardForm(f => ({ ...f, description: e.target.value }))} className="w-full px-3 py-2 rounded-lg border border-gray-200 dark:border-gray-700 mb-3" />
            <input type="number" placeholder="Costo en puntos" value={rewardForm.points_cost} onChange={e => setRewardForm(f => ({ ...f, points_cost: e.target.value }))} className="w-full px-3 py-2 rounded-lg border border-gray-200 dark:border-gray-700 mb-3" />

            <div className="mb-4">
              <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1.5">Categoría</label>
              <div className="flex gap-2">
                <select value={rewardForm.category} onChange={e => setRewardForm(f => ({ ...f, category: e.target.value }))} className="flex-1 px-3 py-2 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800">
                  {(categories.length ? categories : [{ id: 'def', name: 'general' }]).map(c => (
                    <option key={c.id} value={c.name}>{c.name}</option>
                  ))}
                </select>
                <button type="button" onClick={() => setCategoryModal(true)} className="px-3 py-2 rounded-lg border border-gray-200 dark:border-gray-700 text-sm">+</button>
              </div>
            </div>

            <div className="flex gap-2">
              <button onClick={() => !saving && setRewardModal({ show: false, editing: null })} className="flex-1 py-2 rounded-lg border">Cancelar</button>
              <button onClick={saveReward} disabled={saving} className="flex-1 py-2 rounded-lg bg-indigo-600 text-white disabled:opacity-50">Guardar</button>
            </div>
          </div>
        </div>
      )}

      {/* Modal nivel */}
      {levelModal.show && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 overflow-y-auto" onClick={() => !saving && setLevelModal({ show: false, editing: null })}>
          <div className="bg-white dark:bg-gray-900 rounded-2xl p-6 max-w-md w-full my-8" onClick={e => e.stopPropagation()}>
            <h3 className="text-lg font-semibold mb-4">{levelModal.editing ? 'Editar nivel' : 'Nuevo nivel'}</h3>
            <input type="text" placeholder="Nombre" value={levelForm.level_name || ''} onChange={e => setLevelForm(f => ({ ...f, level_name: e.target.value }))} className="w-full px-3 py-2 rounded-lg border border-gray-200 dark:border-gray-700 mb-3" />
            <input type="number" placeholder="Puntos mínimos" value={levelForm.min_points ?? ''} onChange={e => setLevelForm(f => ({ ...f, min_points: parseInt(e.target.value) || 0 }))} className="w-full px-3 py-2 rounded-lg border border-gray-200 dark:border-gray-700 mb-3" />
            <input type="number" placeholder="Puntos máximos (vacío = sin límite)" value={levelForm.max_points ?? ''} onChange={e => setLevelForm(f => ({ ...f, max_points: e.target.value ? parseInt(e.target.value) : null }))} className="w-full px-3 py-2 rounded-lg border border-gray-200 dark:border-gray-700 mb-3" />
            <div className="mb-4">
              <label className="block text-xs text-gray-500 mb-1">Beneficios (uno por línea)</label>
              <textarea rows={3} value={(levelForm.benefits || []).join('\n')} onChange={e => setLevelForm(f => ({ ...f, benefits: e.target.value.split('\n').filter(Boolean) }))} placeholder="Beneficio 1&#10;Beneficio 2" className="w-full px-3 py-2 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800" />
            </div>
            <div className="grid grid-cols-2 gap-3 mb-4">
              <div>
                <label className="block text-xs text-gray-500 mb-1">Color principal</label>
                <input type="color" value={levelForm.color_primary || '#6366f1'} onChange={e => setLevelForm(f => ({ ...f, color_primary: e.target.value }))} className="w-full h-10 rounded border" />
              </div>
              <div>
                <label className="block text-xs text-gray-500 mb-1">Color secundario</label>
                <input type="color" value={levelForm.color_secondary || '#ffffff'} onChange={e => setLevelForm(f => ({ ...f, color_secondary: e.target.value }))} className="w-full h-10 rounded border" />
              </div>
            </div>
            <div className="flex gap-2">
              <button onClick={() => !saving && setLevelModal({ show: false, editing: null })} className="flex-1 py-2 rounded-lg border">Cancelar</button>
              <button onClick={saveLevel} disabled={saving} className="flex-1 py-2 rounded-lg bg-indigo-600 text-white disabled:opacity-50">Guardar</button>
            </div>
          </div>
        </div>
      )}

      {/* Modal nueva categoría */}
      {categoryModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50" onClick={() => !saving && setCategoryModal(false)}>
          <div className="bg-white dark:bg-gray-900 rounded-2xl p-6 max-w-sm w-full" onClick={e => e.stopPropagation()}>
            <h3 className="text-lg font-semibold mb-4">Nueva categoría</h3>
            <input type="text" placeholder="Nombre (ej: experiencias, productos)" value={newCategoryName} onChange={e => setNewCategoryName(e.target.value)} className="w-full px-3 py-2 rounded-lg border border-gray-200 dark:border-gray-700 mb-4" />
            <div className="flex gap-2">
              <button onClick={() => !saving && setCategoryModal(false)} className="flex-1 py-2 rounded-lg border">Cancelar</button>
              <button onClick={addCategory} disabled={saving || !newCategoryName.trim()} className="flex-1 py-2 rounded-lg bg-indigo-600 text-white disabled:opacity-50">Crear</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default EmployeePointsConfig;
